import {
  PlayerCreationEntitiesResponseDto,
  TeamSelectableEntityResponseDto,
} from "@/lib/api/interfaces/players.interface";
import {
  usePlayersCreate,
  usePlayersCreationEntities,
  usePlayersUploadImage,
  usePlayersApplyImageEdit,
  usePlayersPreviewBackgroundRemoval,
} from "@/lib/api/requests/players.requests";
import { toast } from "@/lib/toast";
import { PLAYER_POSITIONS } from "@/lib/utils/player-utils";
import { Checkbox } from "@/ui-components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { MultiSelect } from "@/ui-components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { Textarea } from "@/ui-components/ui/textarea";
import { CreateBaseDialog, DialogFormSection } from "@/util-components/dialog/base-dialogs";
import { ManagedImageField, persistManagedImageDraft } from "@/util-components/media";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Calendar,
  Flag,
  Hash,
  IdCardLanyard,
  Image as ImageIcon,
  LibraryBig,
  Loader2,
  Quote,
  Scale,
  Shirt,
  User,
  UserPlus,
  Users,
} from "lucide-react";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

export const parseTeamsToSelectOptions = (teams: TeamSelectableEntityResponseDto[]) =>
  teams.map((team) => ({
    label: team.value,
    value: team._id,
  }));

const createPlayerSchema = z.object({
  firstName: z.string().min(1, { message: "Vorname ist erforderlich" }),
  lastName: z.string().min(1, { message: "Nachname ist erforderlich" }),
  jerseyNumber: z.number().optional(),
  birthDate: z.string().min(1, { message: "Geburtsdatum ist erforderlich" }),
  position: z.string().optional(),
  birthDateConsent: z.boolean(),
  nickname: z.string().optional(),
  biography: z.string().optional(),
  height: z.number().optional(),
  weight: z.number().optional(),
  belongsToTeamId: z.string().min(1, { message: "Hauptmannschaft ist erforderlich" }),
  secondaryTeamIds: z.array(z.string()).optional(),
  nationality: z.string().optional(),
  image: z.instanceof(File).optional(),
});

type CreatePlayerFormValues = z.infer<typeof createPlayerSchema>;

const CreatePlayerDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: (refetchData: boolean) => void;
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [canSave, setCanSave] = useState(false);

  return (
    <CreateBaseDialog
      open={open}
      onClose={onClose}
      icon={UserPlus}
      title="Spieler erstellen"
      description="Stammdaten, Details und Spielerbild eintragen."
      isSaving={isSaving}
      canSave={canSave}
      formId="create-player-form"
    >
      <Suspense
        fallback={
          <div className="flex h-full items-center justify-center p-12">
            <Loader2 className="size-8 animate-spin text-blue-600" />
          </div>
        }
      >
        {open ? (
          <CreatePlayerFetcher onClose={onClose} setIsSaving={setIsSaving} setCanSave={setCanSave} />
        ) : null}
      </Suspense>
    </CreateBaseDialog>
  );
};

const CreatePlayerFetcher = ({
  onClose,
  setIsSaving,
  setCanSave,
}: {
  onClose: (refetchData: boolean) => void;
  setIsSaving: (value: boolean) => void;
  setCanSave: (value: boolean) => void;
}) => {
  const { data: creationEntities } = usePlayersCreationEntities();

  return (
    <CreatePlayerForm
      creationEntities={creationEntities}
      setIsSaving={setIsSaving}
      setCanSave={setCanSave}
      onFormSubmit={() => onClose(true)}
    />
  );
};

const CreatePlayerForm = ({
  creationEntities,
  setIsSaving,
  setCanSave,
  onFormSubmit,
}: {
  creationEntities: PlayerCreationEntitiesResponseDto;
  setIsSaving: (value: boolean) => void;
  setCanSave: (value: boolean) => void;
  onFormSubmit: () => void;
}) => {
  const { mutateAsync: createPlayer } = usePlayersCreate();
  const { mutateAsync: uploadImage } = usePlayersUploadImage();
  const { mutateAsync: applyImageEdit } = usePlayersApplyImageEdit();
  const { mutateAsync: previewBackgroundRemoval } = usePlayersPreviewBackgroundRemoval();

  const form = useForm<CreatePlayerFormValues>({
    resolver: zodResolver(createPlayerSchema),
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      birthDate: "",
      birthDateConsent: false,
      nickname: "",
      biography: "",
      height: undefined,
      weight: undefined,
      position: undefined,
      nationality: "",
      belongsToTeamId: "",
      secondaryTeamIds: [],
      image: undefined,
    },
  });

  const belongsToTeamId = form.watch("belongsToTeamId");
  const imageFile = form.watch("image");

  const secondaryTeamOptions = useMemo(
    () => parseTeamsToSelectOptions(creationEntities.teams.filter((team) => team._id !== belongsToTeamId)),
    [creationEntities.teams, belongsToTeamId]
  );

  useEffect(() => {
    const selectedSecondaryTeamIds = form.getValues("secondaryTeamIds") ?? [];
    if (selectedSecondaryTeamIds.includes(belongsToTeamId)) {
      form.setValue(
        "secondaryTeamIds",
        selectedSecondaryTeamIds.filter((teamId) => teamId !== belongsToTeamId),
        { shouldDirty: true, shouldValidate: true }
      );
    }
  }, [belongsToTeamId, form]);

  useEffect(() => {
    setCanSave(form.formState.isValid);
  }, [form.formState.isValid, setCanSave]);

  const onSubmit = async (values: CreatePlayerFormValues) => {
    setIsSaving(true);

    try {
      const createdPlayer = await createPlayer({
        firstName: values.firstName,
        lastName: values.lastName,
        birthDate: `${values.birthDate}T00:00:00.000Z`,
        birthDateConsent: values.birthDateConsent,
        jerseyNumber: values.jerseyNumber ?? undefined,
        nickname: values.nickname || undefined,
        biography: values.biography || undefined,
        height: values.height,
        weight: values.weight,
        position: values.position || undefined,
        belongsToTeamId: values.belongsToTeamId,
        nationality: values.nationality || undefined,
        secondaryTeamIds: values.secondaryTeamIds?.length ? values.secondaryTeamIds : undefined,
      });

      if (values.image && createdPlayer._id) {
        await persistManagedImageDraft({
          entityId: createdPlayer._id,
          file: values.image,
          uploadImage,
          applyImageEdit,
        });
      }

      toast.success("Spieler wurde erfolgreich erstellt");
      onFormSubmit();
    } catch (error) {
      toast.error("Spieler konnte nicht erstellt werden", { error });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Form {...form}>
      <form id="create-player-form" onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto p-4 sm:p-6">
          <DialogFormSection title="Spielerbild" icon={ImageIcon}>
            <ManagedImageField
              file={imageFile}
              onFileChange={(nextFile) => form.setValue("image", nextFile ?? undefined, { shouldDirty: true, shouldValidate: true })}
              disabled={form.formState.isSubmitting}
              busyAction={form.formState.isSubmitting ? "saving" : null}
              previewBackgroundRemoval={async (file) => previewBackgroundRemoval({ file })}
            />
          </DialogFormSection>

          <DialogFormSection title="Stammdaten" icon={IdCardLanyard}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isOptional={false}>
                      <User className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Vorname
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Max" {...field} className="h-11 bg-white" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isOptional={false}>
                      <User className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Nachname
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Mustermann" {...field} className="h-11 bg-white" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="birthDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isOptional={false}>
                      <Calendar className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Geburtsdatum
                    </FormLabel>
                    <FormControl>
                      <Input type="date" {...field} className="h-11 bg-white" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="position"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Shirt className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Position
                    </FormLabel>
                    <Select onValueChange={(value) => field.onChange(value === "none" ? undefined : value)} value={field.value ?? "none"}>
                      <FormControl>
                        <SelectTrigger className="h-11 bg-white">
                          <SelectValue placeholder="Position wählen" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Keine Position</SelectItem>
                        {PLAYER_POSITIONS.map((playerPosition) => (
                          <SelectItem key={playerPosition.value} value={playerPosition.value}>
                            {playerPosition.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DialogFormSection>

          <DialogFormSection title="Teams" icon={Users}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="belongsToTeamId"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel isOptional={false}>
                      <Users className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Hauptmannschaft
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-11 bg-white">
                          <SelectValue placeholder="Wähle die Hauptmannschaft" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {creationEntities.teams.map((team) => (
                          <SelectItem key={team._id} value={team._id}>
                            {team.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="secondaryTeamIds"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>
                      <Users className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Zusätzliche Mannschaften
                    </FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={secondaryTeamOptions}
                        onValueChange={field.onChange}
                        value={field.value}
                        placeholder="Wähle zusätzliche Mannschaften"
                        className="py-1 bg-white min-h-[44px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DialogFormSection>

          <DialogFormSection title="Details" icon={LibraryBig}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="jerseyNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Hash className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Rückennummer
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="11"
                        {...field}
                        type="number"
                        className="h-11 bg-white"
                        onChange={(event) => field.onChange(event.target.value ? Number(event.target.value) : undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nickname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Quote className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Spitzname
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Max" {...field} className="h-11 bg-white" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nationality"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Flag className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Nationalität
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Deutschland" {...field} className="h-11 bg-white" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4 sm:col-span-2">
                <FormField
                  control={form.control}
                  name="height"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <Scale className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                        Größe (cm)
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="180"
                          {...field}
                          type="number"
                          className="h-11 bg-white"
                          onChange={(event) => field.onChange(event.target.value ? Number(event.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="weight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <Scale className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                        Gewicht (kg)
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="80"
                          {...field}
                          type="number"
                          className="h-11 bg-white"
                          onChange={(event) => field.onChange(event.target.value ? Number(event.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="biography"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Biographie</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Erzähle etwas über den Spieler..." className="min-h-[96px] resize-none bg-white" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="birthDateConsent"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2 flex flex-row items-center gap-3 space-y-0 rounded-xl border bg-white px-4 py-3">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="leading-tight">
                      <FormLabel>Geburtstags-Posts erlaubt?</FormLabel>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Wenn aktiv, können Geburtstagsposts automatisch erstellt werden.
                      </p>
                    </div>
                  </FormItem>
                )}
              />
            </div>
          </DialogFormSection>
        </div>
      </form>
    </Form>
  );
};

export default CreatePlayerDialog;
